/**
 * 応答タブ（Issue #467）。**盤に触らず、下半分だけで core に答える**。中身は今の問い合わせ 1 つ（サーバーの input.query.view。Issue #665）。
 *   上: 誰に（色）・何を（見出しと案内文）
 *   中: その問い合わせの選択肢だけ。
 *       カードの行動・発動・カードの選択は**一覧性重視**の 1 行 1 枚（小さい画像・名前・場所の文字。CardRows）。行動はその行のボタンで送る。
 *       **盤（ゾーン）で選ぶのは置く場所を決めるときだけ**で、止めた動画のフレームの上でゾーンを押す（PlaceOnVideo）
 *   下（固定）: 戻す / ▶ 再生（動画を再生して再生タブへ。onPlay）/ パス（チェーンしない・やめる・終える）/ 決定
 *   登録待ち（view.type 'hold'。Issue #612）: 未確認のカードが出たイベントでサービスが止めている。項目と「登録する」（onRegister）だけを出す
 *   extra: 選択肢の下に足すもの（入力画面は「さっき通したところ（戻る）」。Issue #658）。置く場所の選択（高さいっぱい）のときは本体の下に別に
 * 送るのはページ: 行動などのボタンは onRespond(payload)（その payload を持つ選択肢）、選択・並べ替えなどは onAnswer({ picks | value | mask | places })。
 * どちらもページがサーバーの問い合わせの答え（OcgAnswer）にする（payload を組むのはサーバー）。カードの選択で最後に選んだカードは onFocus(boardKey | null)。
 * 選択の途中の状態はこの部品が持つ。問い合わせが変わったらページが key で作り直す。
 */
import { useEffect, useMemo, useState } from 'react';
import { playerColor, usePlayerColors } from './common';
import { PromptDialog } from './PromptDialog';
import CardRows, { CardRow } from './CardRows';
import PlaceOnVideo from './PlaceOnVideo';
import { ready, toggle } from './selectRules';

export const btn = (variant = 'plain', { grow = false } = {}) => ({
  minHeight: 44, padding: '8px 12px', borderRadius: 10, fontSize: 15, cursor: 'pointer', color: '#e8eefc', flex: grow ? '1 1 0' : '0 0 auto', minWidth: 0,
  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  border: `1px solid ${variant === 'primary' ? '#4fc3ff' : variant === 'danger' ? '#c65' : '#345'}`,
  background: variant === 'primary' ? '#1b4a6d' : variant === 'danger' ? '#3a1c1c' : '#151c30',
});

const allTiles = (groups) => (groups ?? []).flatMap((g) => g.tiles);

export default function RespondTab({ view, query = null, board = null, names = {}, thumbs = {}, players = [], busy = false, canUndo = false,
  videoEl = null, videoSize = null, zones = null, onRespond, onAnswer, onUndo, onPlay, onFocus, onPlaces, onRegister = null, extra = null, children = null,
  picked: pickedProp = null, onPicked = null }) {
  // カードの選択: tile.index。席モードは入力画面が持つ（2 席で共有。Issue #717）。渡されなければここで持つ
  const [pickedLocal, setPickedLocal] = useState([]);
  const picked = pickedProp ?? pickedLocal;
  const setPicked = (f) => { const next = typeof f === 'function' ? f(picked) : f; if (onPicked) onPicked(next); else setPickedLocal(next); };
  const colors = usePlayerColors();
  const tiles = useMemo(() => allTiles(view?.groups), [view]);
  // 上半分の印: カードの選択は最後に選んだカード、効果の発動確認はそのカード
  useEffect(() => {
    if (!onFocus) return;
    if (view?.type === 'select') { const last = tiles.find((t) => t.index === picked[picked.length - 1]); onFocus(last?.boardKey ?? null); return; }
    onFocus(view?.type === 'effectyn' ? (view.tile?.boardKey ?? null) : null);
  }, [view, picked, tiles, onFocus]);

  const player = view?.player ?? 0;
  const who = players?.[player]?.name ?? `P${player + 1}`;
  const send = (payload) => { if (!busy && payload) onRespond?.(payload); };
  const answer = (intent) => { if (!busy) onAnswer?.(intent); };

  let body = null;
  let fill = false;   // 中身を余白なしで高さいっぱいに（置く場所の選択）
  if (!view || view.type === 'none') {
    body = <div style={{ opacity: 0.75, fontSize: 14 }}>今は答える問い合わせがありません</div>;
  } else if (view.type === 'hold') {
    const { item } = view;
    body = (
      <div data-testid="respond-hold" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ fontSize: 14 }}>未登録のカードが出たので、ここで止めています。登録すると先へ進みます。</div>
        <div style={{ fontSize: 13, opacity: 0.9 }}>{item.label}: <strong>{item.value_text}</strong>{item.confirmed ? '' : '（仮）'}</div>
        <button type="button" data-testid="respond-register" disabled={busy || !onRegister} style={btn('primary')} onClick={onRegister}>登録する</button>
      </div>
    );
  } else if (view.type === 'command' || view.type === 'chain') {
    body = (
      <>
        {view.phase?.length ? (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 6, scrollSnapAlign: 'start' }}>
            {view.phase.map((p) => <button key={p.key} type="button" disabled={busy} style={{ ...btn('primary', { grow: true }), minHeight: 40 }} onClick={() => send(p.payload)}>{p.label}</button>)}
          </div>
        ) : null}
        <CardRows groups={view.groups} player={player} names={names} thumbs={thumbs} busy={busy} onAction={send} />
        {!view.groups.length ? <div style={{ opacity: 0.75, fontSize: 13 }}>{view.type === 'chain' ? 'チェーンできるカードはありません' : '行動できるカードはありません'}</div> : null}
      </>
    );
  } else if (view.type === 'select') {
    const { spec } = view;
    const badgeOf = (t) => (picked.includes(t.index) ? picked.indexOf(t.index) + 1 : (t.sum != null ? t.sum : (t.weight > 1 ? `×${t.weight}` : (t.selected ? '済' : null))));
    body = (
      <>
        {spec.note ? <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 4 }}>{spec.note}</div> : null}
        <CardRows mode="select" groups={view.groups} player={player} names={names} thumbs={thumbs} busy={busy} picked={picked} badgeOf={badgeOf}
          onToggle={(t) => setPicked((p) => toggle({ picks: p }, query, t.index).picks)} />
      </>
    );
  } else if (view.type === 'place') {
    fill = true;
    body = <PlaceOnVideo options={view.options} count={view.count} videoEl={videoEl} videoSize={videoSize} zones={zones} board={board} thumbs={thumbs} busy={busy}
      onPicking={onPlaces} onDone={(places) => { onPlaces?.([]); answer({ places }); }} />;
  } else if (view.type === 'effectyn') {
    body = (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <CardRow tile={view.tile} player={player} names={names} thumbs={thumbs} />
      </div>
    );
  } else if (view.type === 'prompt') {
    body = <PromptDialog dialog={{ title: view.title ?? view.kind, player, hint: null, spec: view.spec }} players={players} busy={busy} onRespond={send} onAnswer={answer} area={{ inline: true }} />;
  }

  // 固定の列の右側（パス・やめる・終える・決定・はい/いいえ）
  const tail = [];
  if (view?.type === 'chain' && view.pass) tail.push(<button key="pass" type="button" disabled={busy} style={btn('plain', { grow: true })} onClick={() => send(view.pass)}>チェーンしない</button>);
  if (view?.type === 'select') {
    const { spec } = view;
    if (spec.cancel) tail.push(<button key="cancel" type="button" disabled={busy} style={btn('plain')} onClick={() => send(spec.cancel)}>やめる</button>);
    if (spec.finish) tail.push(<button key="finish" type="button" disabled={busy} style={btn('plain')} onClick={() => send(spec.finish)}>終える</button>);
    tail.push(<button key="ok" type="button" data-testid="respond-decide" disabled={busy || !ready({ picks: picked }, query)} style={btn('primary', { grow: true })} onClick={() => answer({ picks: picked })}>決定（{picked.length}）</button>);
  }
  if (view?.type === 'effectyn') {
    tail.push(<button key="no" type="button" disabled={busy} style={btn('plain', { grow: true })} onClick={() => send(view.no)}>発動しない</button>);
    tail.push(<button key="yes" type="button" disabled={busy} style={btn('primary', { grow: true })} onClick={() => send(view.yes)}>発動する</button>);
  }

  return (
    <div data-testid="respond-tab" data-type={view?.type ?? 'none'} style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {children}
      {view && view.type !== 'none' ? (
        <div data-testid="respond-head" style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '5px 10px', borderBottom: '1px solid #1c2440', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 12, height: 12, borderRadius: 999, background: playerColor(player, colors) ?? colors[0], flexShrink: 0 }} />
            <span style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{who}</span>
            <span style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{view.kind}</span>
          </div>
          {/* ADS と同じく「直前に起きたこと」はチェーン確認・発動確認のときだけ、「今やること」の上に（Issue #631） */}
          {view.context ? <div data-testid="respond-context" style={{ fontSize: 13 }}>{view.context}</div> : null}
          {view.title ? <strong data-testid="respond-title" style={{ fontSize: 14 }}>{view.title}</strong> : null}
        </div>
      ) : null}
      {/* スクロールは行の頭でカチッと止める（中途半端な位置で止めない） */}
      <div data-testid="respond-body" style={{ flex: 1, minHeight: 0, overflowY: fill ? 'hidden' : 'auto', padding: fill ? 6 : 8, boxSizing: 'border-box',
        scrollSnapType: fill ? undefined : 'y mandatory', scrollPaddingTop: 8, overscrollBehavior: 'contain' }}>{body}{fill ? null : extra}</div>
      {fill && extra ? <div style={{ maxHeight: '35%', overflowY: 'auto', padding: '0 8px', flexShrink: 0, overscrollBehavior: 'contain' }}>{extra}</div> : null}
      <div style={{ display: 'flex', gap: 8, padding: '8px 10px', borderTop: '1px solid #1c2440', flexShrink: 0 }}>
        <button type="button" data-testid="respond-undo" disabled={busy || !canUndo} style={btn('plain')} onClick={onUndo}>戻す</button>
        {onPlay ? <button type="button" data-testid="respond-play" style={btn('plain')} onClick={onPlay} aria-label="再生して再生タブへ">▶ 再生</button> : null}
        {tail}
      </div>
    </div>
  );
}
